import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Calculator } from "lucide-react";
import { toast } from "sonner";
import { CalculatorSuperCalcCTA } from "@/components/CalculatorSuperCalcCTA";
import { calculatorCTAs } from "@/data/calculatorCTAs";
import { useLanguageAwareSEO } from "@/hooks/useLanguageAwareSEO";
import { breadcrumbSchema, calculatorToolSchema } from "@/utils/structuredData";

export default function CarnotEfficiencyCalculator() {
  const [hotTemp, setHotTemp] = useState("");

  useLanguageAwareSEO([
    (lang) => breadcrumbSchema([
      { name: "Home", url: "https://calclab.app" },
      { name: "Physics", url: "https://calclab.app/physics" },
      { name: "Carnot Efficiency Calculator", url: "https://calclab.app/calculator/carnot-efficiency" }
    ], lang),
    (lang) => calculatorToolSchema(
      "Carnot Efficiency Calculator",
      "Calculate maximum theoretical efficiency of heat engines. Free online thermodynamics calculator.",
      "https://calclab.app/calculator/carnot-efficiency",
      lang
    )
  ]);
  const [coldTemp, setColdTemp] = useState("");
  const [result, setResult] = useState<number | null>(null);

  const calculateEfficiency = () => {
    const th = parseFloat(hotTemp);
    const tc = parseFloat(coldTemp);

    if (isNaN(th) || isNaN(tc)) {
      toast.error("Please enter valid values");
      return;
    }

    if (th <= tc) {
      toast.error("Hot temperature must be greater than cold temperature");
      return;
    }

    if (tc <= 0 || th <= 0) {
      toast.error("Temperatures must be in Kelvin (> 0)");
      return;
    }

    const efficiency = (1 - tc / th) * 100;
    setResult(efficiency);
    toast.success("Calculation complete!");
  };

  const reset = () => {
    setHotTemp("");
    setColdTemp("");
    setResult(null);
  };

  return (
    <PageLayout calculatorName="Carnot Efficiency Calculator" calculatorPath="/calculator/carnot-efficiency">
      <div className="max-w-2xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold mb-2">Carnot Efficiency Calculator</h1>
          <p className="text-muted-foreground">
            Maximum theoretical efficiency of a heat engine
          </p>
        </div>

        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle>Formula: η = 1 - T_c/T_h</CardTitle>
            <CardDescription>
              Carnot efficiency depends only on reservoir temperatures
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="hotTemp">Hot Reservoir Temperature T_h (K)</Label>
                <Input
                  id="hotTemp"
                  type="number"
                  step="any"
                  placeholder="Enter hot temperature"
                  value={hotTemp}
                  onChange={(e) => setHotTemp(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">Must be in Kelvin (T_°C + 273.15)</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="coldTemp">Cold Reservoir Temperature T_c (K)</Label>
                <Input
                  id="coldTemp"
                  type="number"
                  step="any"
                  placeholder="Enter cold temperature"
                  value={coldTemp}
                  onChange={(e) => setColdTemp(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">Must be less than T_h</p>
              </div>
            </div>

            <div className="flex gap-3">
              <Button onClick={calculateEfficiency} className="flex-1">
                <Calculator className="mr-2 h-4 w-4" />
                Calculate
              </Button>
              <Button onClick={reset} variant="outline">
                Reset
              </Button>
            </div>

            {result !== null && (
              <>
                <Card className="bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20">
                  <CardHeader>
                    <CardTitle className="text-xl">Result</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-3xl font-bold text-primary">
                      {result.toFixed(2)}%
                    </p>
                    <p className="text-sm text-muted-foreground mt-2">
                      Maximum Carnot Efficiency
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Real engines are always less efficient
                    </p>
                  </CardContent>
                </Card>
                
                <CalculatorSuperCalcCTA config={calculatorCTAs["carnot-efficiency"]} trigger={result} />
              </>
            )}

            <Card className="bg-muted/50">
              <CardHeader>
                <CardTitle className="text-lg">Key Concepts</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm text-muted-foreground">
                <p>• Carnot cycle is the most efficient possible</p>
                <p>• Real engines cannot reach Carnot efficiency</p>
                <p>• Higher T_h or lower T_c increases efficiency</p>
                <p>• 100% efficiency impossible (T_c cannot be 0 K)</p>
                <p className="pt-2 font-medium text-foreground">
                  Example: T_h = 600 K, T_c = 300 K → η = 50%
                </p>
              </CardContent>
            </Card>
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
}
